import React, { useMemo } from 'react';
import { useFormikContext } from 'formik';
import { isEmpty } from 'lodash';

import ListItemText from '~/components/core/Atomic/ListItemText';
import MenuItem from '~/components/core/Atomic/MenuItem';
import Tooltip from '~/components/core/Atomic/Tooltip';
import Text from '~/components/core/TextComponents/Text';
import TextFieldFormikUntyped from '~/components/TextFieldFormik';
import { SlaTemplateItemListSlaTemplateSource } from '~/server_shared/openapi/data-contracts';
import { stringCmp } from '~/Utils';

import { ENTITY_LEVEL_LABELS, TEXTS } from '../texts';
import type { SlaTemplateItem } from '../types';

import type { SlaRuleFormValues } from './formValues';

interface TextFieldFormikProps {
  id: string;
  label: string;
  select?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  autoSelectSingleOption?: boolean;
  children?: React.ReactNode;
}
// Assertion over a still-untyped JS component, which the TS rule allows for gradual migration: it
// only narrows the props this file passes, so a wrong prop name or type is still a compile error.
const TextFieldFormik = TextFieldFormikUntyped as React.ComponentType<TextFieldFormikProps>;

interface TemplatePickerFieldProps {
  templates: SlaTemplateItem[];
  disabled?: boolean;
}

/** Binds `metabase_card_id`. The groups follow the source enum's own order, which its docstring fixes
 *  as the curated system cards first and the organization's folder after. */
const TemplatePickerField: React.FC<TemplatePickerFieldProps> = ({ templates, disabled }) => {
  const { values } = useFormikContext<SlaRuleFormValues>();

  const renderTemplate = (template: SlaTemplateItem) => {
    // An edited rule whose card broke since it was saved keeps that card enabled: it is the current
    // pick, and MUI opens the menu on the selected row only when that row is enabled. The save then
    // fails with the contract reason the backend reports.
    const isSelectable = template.is_valid || template.card_id === values.metabase_card_id;
    return (
      <MenuItem key={template.card_id} value={template.card_id} disabled={!isSelectable}>
        {template.is_valid ? (
          template.name
        ) : (
          // On a span because a disabled item sets pointer-events: none, which would keep the tooltip
          // from ever opening. The reasons describe the option and must not name it: MUI copies a
          // string title onto its child as `title` or `aria-label`, which then becomes the option's
          // accessible name, so the title is a node and `describeChild` links it only while open.
          <Tooltip title={<>{(template.invalid_reasons ?? []).join('; ')}</>} describeChild>
            <span style={{ pointerEvents: 'auto' }}>{TEXTS.TEMPLATE_UNAVAILABLE_NAME(template.name)}</span>
          </Tooltip>
        )}
      </MenuItem>
    );
  };

  const selectedTemplate = templates.find((template) => template.card_id === values.metabase_card_id);

  // The field re-renders on every form change, and the list only changes when it is refetched.
  const groupedTemplates = useMemo(
    () =>
      Object.values(SlaTemplateItemListSlaTemplateSource).map((source) => ({
        source,
        group: templates.filter((template) => template.source === source).sort((a, b) => stringCmp(a.name, b.name)),
      })),
    [templates]
  );

  return (
    <div className="mb-20">
      <TextFieldFormik
        id="metabase_card_id"
        label={TEXTS.TEMPLATE_LABEL}
        select
        fullWidth
        disabled={disabled}
        // A lone card, possibly one that breaks the contract, must never be picked for the admin.
        autoSelectSingleOption={false}
      >
        {groupedTemplates.flatMap(({ source, group }) => {
          if (isEmpty(group)) {
            return [];
          }
          return [
            // The group header row of `MultiSelectIdLabelTextFieldFormik`: no value, and disabled so
            // MUI skips it in keyboard navigation.
            <MenuItem key={`header-${source}`} disabled className="!min-h-0 !py-4 !opacity-100">
              <ListItemText
                primary={TEXTS.TEMPLATE_SOURCE_LABELS[source]}
                primaryTypographyProps={{ className: '!text-xs !font-semibold uppercase !text-slate-700' }}
              />
            </MenuItem>,
            ...group.map(renderTemplate),
          ];
        })}
      </TextFieldFormik>
      {selectedTemplate?.entity_level ? (
        <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {TEXTS.ENTITY_LEVEL(ENTITY_LEVEL_LABELS[selectedTemplate.entity_level])}
        </Text>
      ) : null}
    </div>
  );
};

export default TemplatePickerField;
